import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Mail, Phone, Globe, MapPin,ChevronLeft, ChevronRight, Building2,Users,Briefcase } from "lucide-react";
import { CompanyAdmin } from "@/types/crm";

interface CompaniesAdminTableProps {
  companies: CompanyAdmin[];
  onEdit: (company: CompanyAdmin) => void;
  onDelete: (company: CompanyAdmin) => void;
}

export function CompaniesAdminTable({ companies, onEdit, onDelete }: CompaniesAdminTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const totalPages = Math.ceil(companies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCompanies = companies.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleDelete = (company: CompanyAdmin) => {
    if (company.estado === 0) {
      alert("Esta empresa ya está inactiva");
      return;
    }

    if (company.totalUsuarios && company.totalUsuarios > 0) {
      alert(`No se puede desactivar la empresa "${company.name}" porque tiene ${company.totalUsuarios} usuarios activos`);
      return;
    }

    if (confirm(`¿Estás seguro de desactivar la empresa "${company.name}"?`)) {
      onDelete(company);
    }
  };

  const getInitials = (name: string): string => {
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Ubicación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Estadísticas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {paginatedCompanies.map((company) => (
                <tr key={company.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        {company.logo_url ? (
                          <img 
                            className="h-12 w-12 rounded-lg object-cover border-2 border-slate-200 bg-white p-1" 
                            src={company.logo_url} 
                            alt={company.name}
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white font-semibold border-2 border-slate-200">
                            {getInitials(company.name)}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-slate-900">
                          {company.name}
                        </div>
                        {company.descripcion && (
                          <div className="text-xs text-slate-500 max-w-xs truncate mt-1">
                            {company.descripcion}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {company.email && (
                        <div className="text-sm text-slate-900 flex items-center">
                          <Mail className="h-3 w-3 mr-2 text-slate-400" />
                          <span className="truncate max-w-[200px]">{company.email}</span>
                        </div>
                      )}
                      {company.telefono && (
                        <div className="text-sm text-slate-600 flex items-center">
                          <Phone className="h-3 w-3 mr-2 text-slate-400" />
                          {company.telefono}
                        </div>
                      )}
                      {company.sitio_web && (
                        <div className="text-sm text-blue-600 flex items-center">
                          <Globe className="h-3 w-3 mr-2 text-slate-400" />
                          <a 
                            href={company.sitio_web} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:underline truncate max-w-[180px]"
                          >
                            {company.sitio_web.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {company.direccion ? (
                      <div className="flex items-start text-sm text-slate-600">
                        <MapPin className="h-4 w-4 mr-2 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2 max-w-xs">{company.direccion}</span>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-sm">No especificada</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-slate-600">
                        <Users className="h-4 w-4 mr-2 text-blue-500" />
                        <span className="font-medium">{company.totalUsuarios || 0}</span>
                        <span className="ml-1 text-xs">usuarios</span>
                      </div>
                      <div className="flex items-center text-sm text-slate-600">
                        <Briefcase className="h-4 w-4 mr-2 text-green-500" />
                        <span className="font-medium">{company.totalOportunidades || 0}</span>
                        <span className="ml-1 text-xs">oportunidades</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge 
                      className={
                        company.estado === 1
                          ? "bg-green-100 text-green-800" 
                          : "bg-slate-100 text-slate-800"
                      }
                    >
                      {company.estado === 1 ? "Activa" : "Inactiva"}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(company)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Editar empresa"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(company)}
                        disabled={company.estado === 0 || (company.totalUsuarios && company.totalUsuarios > 0)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          company.estado === 0 
                            ? "Empresa ya inactiva"
                            : company.totalUsuarios && company.totalUsuarios > 0
                            ? `No se puede desactivar (${company.totalUsuarios} usuarios)`
                            : "Desactivar empresa"
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {paginatedCompanies.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              No hay empresas
            </h3>
            <p className="text-slate-600">
              No se encontraron empresas con los filtros aplicados
            </p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Mostrando {startIndex + 1} a {Math.min(endIndex, companies.length)} de {companies.length} empresas
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}